import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export function ArabicCursor() {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
      setIsVisible(true);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    const handleMouseEnter = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInteractive = 
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        !!target.closest('button') ||
        !!target.closest('a') ||
        target.classList.contains('cursor-pointer') ||
        window.getComputedStyle(target).cursor === 'pointer';
      
      setIsHovering(isInteractive);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseover', handleMouseEnter);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseover', handleMouseEnter);
    };
  }, []);

  // Only show on desktop
  if (typeof window !== 'undefined' && window.innerWidth < 768) {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Main cursor - Arabic calligraphy inspired dot */}
          <motion.div
            className="fixed pointer-events-none z-[9999] mix-blend-difference"
            style={{
              left: position.x,
              top: position.y,
              translateX: '-50%',
              translateY: '-50%',
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ 
              scale: isHovering ? 1.5 : 1, 
              opacity: 1,
            }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 28 }}
          >
            <div 
              className={`rounded-full bg-primary transition-all duration-200 ${
                isHovering ? 'w-6 h-6' : 'w-3 h-3'
              }`}
              style={{
                boxShadow: isHovering 
                  ? '0 0 20px hsl(var(--primary) / 0.5)' 
                  : '0 0 10px hsl(var(--primary) / 0.3)',
              }}
            />
          </motion.div>

          {/* Trailing cursor - calligraphy stroke effect */}
          <motion.div
            className="fixed pointer-events-none z-[9998]"
            style={{
              left: position.x,
              top: position.y,
              translateX: '-50%',
              translateY: '-50%',
            }}
            animate={{ 
              scale: isHovering ? 2 : 1,
            }}
            transition={{ type: 'spring', stiffness: 150, damping: 15, mass: 0.5 }}
          >
            <div 
              className={`rounded-full border-2 border-primary/30 transition-all duration-300 ${
                isHovering ? 'w-12 h-12' : 'w-8 h-8'
              }`}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
